/**
 * ConferenciaCustosDialog - Conferência de destino dos custos de um pedido de compra
 * O sistema PREPARA a proposta de vínculo com a Ficha Técnica; o usuário CONFIRMA
 * (com opção de desmembrar quantidades entre ficha, outro projeto e estoque)
 */
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Loader2, Link2, Split, Trash2, CheckCircle2, AlertTriangle, Package, FolderOpen } from 'lucide-react';

const fmtBRL = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);

const ConferenciaCustosDialog = ({ pedido, open, onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [alocacoes, setAlocacoes] = useState({});

  const initAlocacoes = useCallback((d) => {
    const init = {};
    (d.itens || []).forEach((item) => {
      let key;
      if (item.matches.length > 0) {
        const m = item.matches[0];
        key = `ficha:${m.embarcacao_index}:${m.checklist_index}:${m.custo_index}`;
      } else if (d.pedido.projeto_id) {
        key = 'classificar';
      } else {
        key = 'estoque';
      }
      init[item.item_index] = [{ key, quantidade: item.quantidade, projeto_id: '' }];
    });
    setAlocacoes(init);
  }, []);

  useEffect(() => {
    if (!open || !pedido?.id) return;
    setLoading(true);
    setData(null);
    api.get(`/pedidos-compra/${pedido.id}/conferencia-custos`)
      .then((res) => { setData(res.data); initAlocacoes(res.data); })
      .catch((err) => toast.error(err.response?.data?.detail || 'Erro ao carregar conferência de custos'))
      .finally(() => setLoading(false));
  }, [open, pedido?.id, initAlocacoes]);

  const updateDestino = (itemIndex, rowIdx, field, value) => {
    setAlocacoes((prev) => {
      const rows = [...(prev[itemIndex] || [])];
      rows[rowIdx] = { ...rows[rowIdx], [field]: value };
      return { ...prev, [itemIndex]: rows };
    });
  };

  const addDestino = (itemIndex) => {
    setAlocacoes((prev) => ({
      ...prev,
      [itemIndex]: [...(prev[itemIndex] || []), { key: 'estoque', quantidade: 0, projeto_id: '' }],
    }));
  };

  const removeDestino = (itemIndex, rowIdx) => {
    setAlocacoes((prev) => ({
      ...prev,
      [itemIndex]: (prev[itemIndex] || []).filter((_, i) => i !== rowIdx),
    }));
  };

  const somaItem = (item) =>
    (alocacoes[item.item_index] || []).reduce((s, r) => s + (parseFloat(r.quantidade) || 0), 0);

  const itemValido = (item) => Math.abs(somaItem(item) - item.quantidade) < 0.001;
  const tudoValido = data && (data.itens || []).every(itemValido);

  const handleConfirmar = async () => {
    if (!data) return;
    const payload = {
      alocacoes: (data.itens || []).map((item) => ({
        item_index: item.item_index,
        destinos: (alocacoes[item.item_index] || []).map((r) => {
          const qtd = parseFloat(r.quantidade) || 0;
          if (r.key.startsWith('ficha:')) {
            const [, emb, chk, custo] = r.key.split(':');
            return {
              tipo: 'ficha', quantidade: qtd,
              projeto_id: data.pedido.projeto_id,
              embarcacao_index: parseInt(emb),
              checklist_index: parseInt(chk),
              custo_index: parseInt(custo),
            };
          }
          if (r.key === 'classificar') return { tipo: 'classificar', quantidade: qtd, projeto_id: data.pedido.projeto_id };
          if (r.key === 'outro') return { tipo: 'classificar', quantidade: qtd, projeto_id: r.projeto_id };
          return { tipo: 'estoque', quantidade: qtd };
        }).filter((d) => d.quantidade > 0 && (d.tipo !== 'classificar' || d.projeto_id)),
      })),
    };
    setSaving(true);
    try {
      const res = await api.post(`/pedidos-compra/${pedido.id}/confirmar-custos`, payload);
      const { vinculados_ficha, a_classificar, para_estoque } = res.data;
      toast.success(
        `Custos conferidos: ${vinculados_ficha} vinculado(s) à ficha` +
        (a_classificar ? `, ${a_classificar} a classificar` : '') +
        (para_estoque ? `, ${para_estoque} para estoque` : '')
      );
      onSuccess && onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao confirmar custos');
    } finally {
      setSaving(false);
    }
  };

  const matchLabel = (m) => `Ficha: ${m.categoria} › ${m.item_descricao || 'Item'} (plan. ${m.quantidade_planejada} × ${fmtBRL(m.valor_planejado_unitario)})`;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="conferencia-custos-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-emerald-600" />
            Conferir Custos do Projeto
          </DialogTitle>
          <DialogDescription>
            {data?.pedido ? (
              <>Pedido <strong>{data.pedido.numero}</strong>{data.pedido.fornecedor_nome ? ` · ${data.pedido.fornecedor_nome}` : ''}
              {data.pedido.projeto_nome ? <> · Projeto: <strong>{data.pedido.projeto_nome}</strong></> : null}
              — confirme o destino de cada item comprado.</>
            ) : 'Carregando...'}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex justify-center py-10" data-testid="conferencia-loading">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        )}

        {data?.pedido?.custos_conferidos && (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm" data-testid="conferencia-ja-conferido">
            <CheckCircle2 className="w-4 h-4 shrink-0" />
            Os custos deste pedido já foram conferidos. Uma nova confirmação criará vínculos adicionais.
          </div>
        )}

        {data && (
          <div className="space-y-4">
            {(data.itens || []).map((item) => {
              const rows = alocacoes[item.item_index] || [];
              const valido = itemValido(item);
              return (
                <div key={item.item_index} className="border rounded-lg p-3 bg-slate-50/60" data-testid={`conferencia-item-${item.item_index}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <Package className="w-4 h-4 text-slate-500 shrink-0" />
                      <span className="font-medium text-sm text-slate-900 truncate">{item.descricao}</span>
                    </div>
                    <span className="text-xs text-slate-600 whitespace-nowrap">
                      {item.quantidade} × {fmtBRL(item.preco_unitario)} = <strong>{fmtBRL(item.valor_total)}</strong>
                    </span>
                  </div>

                  {item.matches.length === 0 && (
                    <p className="text-[11px] text-amber-700 mb-2 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" />
                      Produto não encontrado na ficha técnica — irá para "Compras a Classificar" do projeto.
                    </p>
                  )}

                  <div className="space-y-2">
                    {rows.map((row, rowIdx) => (
                      <div key={rowIdx} className="flex items-center gap-2 flex-wrap">
                        <Select value={row.key} onValueChange={(v) => updateDestino(item.item_index, rowIdx, 'key', v)}>
                          <SelectTrigger className="flex-1 min-w-[220px] h-8 text-xs bg-white" data-testid={`conferencia-destino-${item.item_index}-${rowIdx}`}>
                            <SelectValue placeholder="Destino" />
                          </SelectTrigger>
                          <SelectContent>
                            {item.matches.map((m) => (
                              <SelectItem key={`ficha:${m.embarcacao_index}:${m.checklist_index}:${m.custo_index}`} value={`ficha:${m.embarcacao_index}:${m.checklist_index}:${m.custo_index}`}>
                                {matchLabel(m)}
                              </SelectItem>
                            ))}
                            {data.pedido.projeto_id && (
                              <SelectItem value="classificar">Compras a Classificar ({data.pedido.projeto_nome || 'projeto do pedido'})</SelectItem>
                            )}
                            <SelectItem value="outro">Outro projeto…</SelectItem>
                            <SelectItem value="estoque">Estoque geral (sem projeto)</SelectItem>
                          </SelectContent>
                        </Select>
                        {row.key === 'outro' && (
                          <Select value={row.projeto_id} onValueChange={(v) => updateDestino(item.item_index, rowIdx, 'projeto_id', v)}>
                            <SelectTrigger className="w-48 h-8 text-xs bg-white" data-testid={`conferencia-projeto-${item.item_index}-${rowIdx}`}>
                              <SelectValue placeholder="Escolha o projeto" />
                            </SelectTrigger>
                            <SelectContent>
                              {(data.projetos || []).filter((p) => p.id !== data.pedido.projeto_id).map((p) => (
                                <SelectItem key={p.id} value={p.id}>{p.nome}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        )}
                        <div className="flex items-center gap-1">
                          <Input
                            type="number" min="0" step="0.01"
                            value={row.quantidade}
                            onChange={(e) => updateDestino(item.item_index, rowIdx, 'quantidade', e.target.value)}
                            className="w-20 h-8 text-xs bg-white"
                            data-testid={`conferencia-qtd-${item.item_index}-${rowIdx}`}
                          />
                          <span className="text-[11px] text-slate-500">un.</span>
                        </div>
                        {rows.length > 1 && (
                          <Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-red-500"
                            onClick={() => removeDestino(item.item_index, rowIdx)}
                            data-testid={`conferencia-remover-destino-${item.item_index}-${rowIdx}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="flex items-center justify-between mt-2">
                    <Button type="button" size="sm" variant="ghost" className="h-7 text-xs text-slate-600"
                      onClick={() => addDestino(item.item_index)}
                      data-testid={`conferencia-dividir-${item.item_index}`}>
                      <Split className="w-3 h-3 mr-1" /> Dividir entre destinos
                    </Button>
                    {!valido && (
                      <Badge className="bg-red-100 text-red-700 hover:bg-red-100 text-[11px]" data-testid={`conferencia-qtd-invalida-${item.item_index}`}>
                        Distribuído: {somaItem(item)} de {item.quantidade}
                      </Badge>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose} data-testid="conferencia-cancelar">Deixar para depois</Button>
          <Button
            onClick={handleConfirmar}
            disabled={!data || !tudoValido || saving}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="conferencia-confirmar"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
            Confirmar Distribuição
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ConferenciaCustosDialog;
